/**
 * profileCrypto — client-side encryption for child PII (Phase 3 Layer B).
 *
 * Founder spec (2026-05-24): the cloud must never see a child's real name,
 * birth date, or school contact. Parents are in control. If a parent
 * forgets their password, they reset it on the site — the encrypted PII
 * is lost (cleared in the same step) and they re-enter it.
 *
 * Recipe:
 *   1. On successful login/register, the cleartext password is run
 *      through PBKDF2 (SHA-256, 250k iterations) with the user's id as
 *      salt → 256-bit AES-GCM key. The derived key lives in
 *      sessionStorage as base64 until the tab/browser closes.
 *   2. PII is written as a single envelope:
 *        { v: 1, iv: <12-byte b64>, ct: <ciphertext b64> }
 *      where the plaintext is `JSON.stringify({name, birth_date, school_contact})`.
 *   3. Server stores ONLY this envelope on `profile.encrypted_pii`. The
 *      plaintext `name` column is set to a non-identifying label ("Board")
 *      so any debug/admin tool that lands on the row sees nothing useful.
 *   4. Reads attempt to decrypt with the cached key. If decryption fails
 *      (e.g. password reset since the row was written) the caller treats
 *      the row as "PII missing — prompt parent to re-enter".
 *
 * This is NOT zero-knowledge — the server could theoretically capture the
 * password in transit. It IS storage-blind: an attacker with full Mongo
 * read access still can't recover names. That's the realistic privacy
 * win for this product surface.
 */

const SESSION_KEY = "sonspeak_pii_key_v1"; // base64 raw 32 bytes
const SESSION_USER = "sonspeak_pii_user_v1"; // user_id this key is for

const ENC = new TextEncoder();
const DEC = new TextDecoder();

function _isWebCrypto() {
  return typeof crypto !== "undefined" && !!crypto.subtle;
}

function _b64encode(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function _b64decode(b64) {
  const s = atob(b64);
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i);
  return bytes;
}

/**
 * Derive a 256-bit AES-GCM key from (password, user_id). Cache the raw
 * key bytes in sessionStorage so subsequent operations don't pay the
 * PBKDF2 cost.
 */
export async function unlockAndCachePiiKey({ password, userId }) {
  if (!_isWebCrypto() || !password || !userId) return false;
  try {
    const passKey = await crypto.subtle.importKey(
      "raw",
      ENC.encode(password),
      { name: "PBKDF2" },
      false,
      ["deriveBits"]
    );
    const bits = await crypto.subtle.deriveBits(
      {
        name: "PBKDF2",
        salt: ENC.encode(`sonspeak-pii-v1:${userId}`),
        iterations: 250000,
        hash: "SHA-256",
      },
      passKey,
      256
    );
    const b64 = _b64encode(bits);
    sessionStorage.setItem(SESSION_KEY, b64);
    sessionStorage.setItem(SESSION_USER, userId);
    return true;
  } catch {
    return false;
  }
}

export function clearPiiKey() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_USER);
  } catch { /* noop */ }
}

function _hasKeyFor(userId) {
  try {
    return sessionStorage.getItem(SESSION_KEY) && sessionStorage.getItem(SESSION_USER) === userId;
  } catch {
    return false;
  }
}

/** True if we have a cached key in this tab for this user. */
export function hasPiiKey(userId) {
  return !!_hasKeyFor(userId);
}

async function _importCachedKey() {
  const b64 = sessionStorage.getItem(SESSION_KEY);
  if (!b64) return null;
  const raw = _b64decode(b64);
  return await crypto.subtle.importKey(
    "raw",
    raw,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"]
  );
}

/**
 * Encrypt a PII object → opaque envelope string. Returns null if the key
 * is missing — caller decides whether to block or fall back.
 */
export async function encryptPii(pii) {
  if (!_isWebCrypto()) return null;
  try {
    const key = await _importCachedKey();
    if (!key) return null;
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const plaintext = ENC.encode(JSON.stringify(pii || {}));
    const ct = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      plaintext
    );
    return JSON.stringify({ v: 1, iv: _b64encode(iv), ct: _b64encode(ct) });
  } catch {
    return null;
  }
}

/**
 * Decrypt an envelope back into a PII object, or null if the key is
 * missing or the envelope was written with a different key (e.g. after
 * a password reset).
 */
export async function decryptPii(envelope) {
  if (!_isWebCrypto() || !envelope || typeof envelope !== "string") return null;
  try {
    const parsed = JSON.parse(envelope);
    if (!parsed || parsed.v !== 1 || !parsed.iv || !parsed.ct) return null;
    const key = await _importCachedKey();
    if (!key) return null;
    const iv = _b64decode(parsed.iv);
    const ct = _b64decode(parsed.ct);
    const pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      ct
    );
    return JSON.parse(DEC.decode(pt));
  } catch {
    return null; // wrong key, tampered blob, etc.
  }
}
